import React, { useEffect, useRef } from "react";

// ---------------------------------------------------------------------------
// Cambia estos imports a los paths reales de tus imágenes de Norris
// ---------------------------------------------------------------------------
import correctImage from "./images/norris_celebrating.png";
import incorrectImage from "./images/norris_suspicious_mad.png";
import gameOverImage from "./images/norris_sad.png";
// ---------------------------------------------------------------------------

export const MODAL_TYPES = {
  CORRECT: "correct",
  INCORRECT: "incorrect",
  GAME_OVER: "gameOver",
};

const MODAL_IMAGES = {
  [MODAL_TYPES.CORRECT]: correctImage,
  [MODAL_TYPES.INCORRECT]: incorrectImage,
  [MODAL_TYPES.GAME_OVER]: gameOverImage,
};

const MODAL_CONFIG = {
  [MODAL_TYPES.CORRECT]: {
    bgColor: "#dcfce7",
    borderColor: "#f9a8d4",
    titleColor: "#b06ab3",
  },
  [MODAL_TYPES.INCORRECT]: {
    bgColor: "#fee2e2",
    borderColor: "#fca5a5",
    titleColor: "#b06ab3",
  },
  [MODAL_TYPES.GAME_OVER]: {
    bgColor: "#fef9c3",
    borderColor: "#fcd34d",
    titleColor: "#b06ab3",
  },
};

const CONFETTI_COLORS = [
  "#f472b6",
  "#facc15",
  "#34d399",
  "#60a5fa",
  "#fb923c",
  "#a78bfa",
  "#f87171",
  "#4ade80",
  "#38bdf8",
  "#fb7185",
];

// ---------------------------------------------------------------------------
// ConfettiCanvas — solo se monta cuando active=true
// ---------------------------------------------------------------------------
const ConfettiCanvas = ({ active }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const pieces = useRef([]);

  useEffect(() => {
    if (!active) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    pieces.current = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * -canvas.height,
      w: 6 + Math.random() * 8,
      h: 6 + Math.random() * 8,
      color:
        CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      speed: 1.5 + Math.random() * 2.5,
      angle: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.15,
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: 0.05 + Math.random() * 0.05,
      shape: Math.random() > 0.5 ? "rect" : "circle",
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.current.forEach((p) => {
        p.y += p.speed;
        p.wobble += p.wobbleSpeed;
        p.angle += p.spin;
        p.x += Math.sin(p.wobble) * 1.2;

        if (p.y > canvas.height + 20) {
          p.y = -20;
          p.x = Math.random() * canvas.width;
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.angle);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.85;

        if (p.shape === "circle") {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }

        ctx.restore();
      });

      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [active]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// GameModal
//
// Props clave:
//   showConfetti={true}  → sopa de letras (confeti al ganar)
//   showConfetti={false} → completa la frase (sin confeti, aparece seguido)
//   showConfetti default = false (opt-in)
// ---------------------------------------------------------------------------
const GameModal = ({
  isOpen,
  onClose,
  type,
  correctAnswer = "",
  bonusMessage = "",
  onNextQuestion,
  onRetry,
  titleOverride,
  messageOverride,
  subMessageOverride,
  buttonTextOverride,
  showConfetti = false,
}) => {
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
      document.body.style.top = "0";
      document.body.style.left = "0";
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = "";
        document.body.style.height = "";
        document.body.style.top = "";
        document.body.style.left = "";
      };
    }
  }, [isOpen]);

  const handleNextQuestion = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onNextQuestion();
  };

  const handleRetry = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onRetry();
  };

  if (!isOpen) return null;

  const isCorrect = type === MODAL_TYPES.CORRECT;
  const config = MODAL_CONFIG[type] ?? MODAL_CONFIG[MODAL_TYPES.CORRECT];

  const renderContent = () => {
    switch (type) {
      case MODAL_TYPES.CORRECT:
        return (
          <>
            <div
              className="rounded-full flex items-center justify-center mb-4 w-[180px] h-[180px] overflow-hidden"
              style={{ background: config.bgColor }}
            >
              <img
                src={MODAL_IMAGES[MODAL_TYPES.CORRECT]}
                alt="¡Correcto!"
                className="w-full h-full object-contain"
              />
            </div>
            <h2
              className="text-xl font-bold mb-1"
              style={{ color: config.titleColor }}
            >
              {titleOverride ?? "¡Ajá! Tú sí sabes, bb"}
            </h2>
            <p className="mb-1 text-gray-500">
              {messageOverride ?? "¡Esa te la sabías!"}
            </p>
            {subMessageOverride && (
              <p
                className="mb-4 font-bold text-sm"
                style={{ color: config.titleColor }}
              >
                {subMessageOverride}
              </p>
            )}
            {Array.isArray(bonusMessage) && bonusMessage.length > 0 && (
              <div className="mb-4 text-yellow-600 text-sm font-semibold animate-pulse space-y-1">
                {bonusMessage.map((msg, idx) => (
                  <p key={idx}>{msg}</p>
                ))}
              </div>
            )}
            <button
              onClick={handleNextQuestion}
              className="px-6 py-2 mt-4 bg-custom-blue text-white rounded-full active:scale-95 transition-transform"
              style={{ touchAction: "manipulation" }}
            >
              {buttonTextOverride ?? "Siguiente"}
            </button>
          </>
        );

      case MODAL_TYPES.INCORRECT:
        return (
          <>
            <div
              className="rounded-full flex items-center justify-center mb-4 w-[180px] h-[180px] overflow-hidden"
              style={{ background: config.bgColor }}
            >
              <img
                src={MODAL_IMAGES[MODAL_TYPES.INCORRECT]}
                alt="Incorrecto"
                className="w-full h-full object-contain"
              />
            </div>
            <h2
              className="text-xl font-bold mb-1"
              style={{ color: config.titleColor }}
            >
              {titleOverride ?? "Mmm... ¿segura, bb?"}
            </h2>
            <p className="mb-6 text-gray-500">
              {messageOverride ?? "Casi, pero no. Inténtalo otra vez."}
            </p>
            <button
              onClick={handleRetry}
              className="px-6 py-2 bg-custom-blue text-white rounded-full active:scale-95 transition-transform"
              style={{ touchAction: "manipulation" }}
            >
              {buttonTextOverride ?? "Reintentar"}
            </button>
          </>
        );

      case MODAL_TYPES.GAME_OVER:
        return (
          <>
            <div
              className="rounded-full flex items-center justify-center mb-4 w-[180px] h-[180px] overflow-hidden"
              style={{ background: config.bgColor }}
            >
              <img
                src={MODAL_IMAGES[MODAL_TYPES.GAME_OVER]}
                alt="Game over"
                className="w-full h-full object-contain"
              />
            </div>
            <h2
              className="text-xl font-bold mb-1 notranslate"
              translate="no"
              lang="es"
              style={{ color: config.titleColor }}
            >
              {titleOverride ?? "¡U\u200Cps!"}
            </h2>
            <p className="mb-3 text-gray-500">
              {messageOverride ?? (
                <>
                  Era "<span className="italic">{correctAnswer}</span>", bb.
                  Pero tú tranqui, no pasa nada.
                </>
              )}
            </p>
            <button
              onClick={handleNextQuestion}
              className="px-6 py-2 bg-custom-blue text-white rounded-full active:scale-95 transition-transform"
              style={{ touchAction: "manipulation" }}
            >
              {buttonTextOverride ?? "Siguiente"}
            </button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        @keyframes norris-modal-in {
          0%   { opacity: 0; transform: scale(0.88) translateY(12px); }
          60%  { transform: scale(1.03) translateY(-2px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .norris-modal {
          animation: norris-modal-in 0.32s cubic-bezier(.22,1,.36,1) both;
        }
      `}</style>

      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-8"
        style={{ touchAction: "none" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Confeti detrás del modal — solo si showConfetti=true Y la respuesta es correcta */}
        <ConfettiCanvas active={isCorrect && showConfetti} />

        <div
          className="norris-modal bg-white shadow-lg w-full max-w-md mx-auto"
          style={{
            border: `2px solid ${config.borderColor}`,
            borderRadius: "20px",
            position: "relative",
            zIndex: 2,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
};

export default GameModal;
